<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSession } from '../stores/session'
import { call } from '../api/http'
import { ledger, type Account, type LedgerSchemas } from '../api/ledger'
import { useForm } from '../lib/form'
import { payables, type PayablesSummary } from '../api/payables'
import { listMatches } from '../api/reconciliation'
import { getOverview, type Overview } from '../api/planning'
import { formatMoney, isoDaysAgo } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiBadge from '../components/UiBadge.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { locale } = useI18n()
const session = useSession()
const load = useForm()
const billsLoad = useForm()

const accounts = ref<Account[]>([])
const summaries = ref<Record<string, LedgerSchemas['Summary']>>({})

// Balances per currency (no FX): one summary call per currency in use.
const currencies = computed(() => [...new Set(accounts.value.filter((a) => a.is_active).map((a) => a.currency))])
const balance = (cur: string) => ({
  currency: cur,
  amount_minor: accounts.value.filter((a) => a.is_active && a.currency === cur).reduce((s, a) => s + a.balance.amount_minor, 0),
})
// Top spending categories: most negative net first.
const topSpending = (cur: string) =>
  (summaries.value[cur]?.by_category ?? [])
    .filter((c) => c.amount.amount_minor < 0)
    .sort((a, b) => a.amount.amount_minor - b.amount.amount_minor)
    .slice(0, 5)

watch(
  () => session.workspace?.id,
  () =>
    load.submit(async () => {
      accounts.value = await call(ledger.GET('/accounts'))
      const range = { from: isoDaysAgo(30), to: isoDaysAgo(0) }
      const list = await Promise.all(
        currencies.value.map((currency) => call(ledger.GET('/summary', { params: { query: { ...range, currency } } }))),
      )
      summaries.value = Object.fromEntries(currencies.value.map((c, i) => [c, list[i]!]))
    }),
  { immediate: true },
)

// Bills + reconciliation cards; loaded apart from the ledger so one failing service does not hide the other.
const bills = ref<PayablesSummary | null>(null)
const suggestions = ref<{ n: number; more: boolean } | null>(null)
watch(
  () => session.workspace?.id,
  () =>
    billsLoad.submit(async () => {
      const [sum, page] = await Promise.all([call(payables.GET('/summary')), listMatches('suggested', undefined, 100)])
      bills.value = sum
      suggestions.value = { n: page.items.length, more: !!page.next_cursor }
    }),
  { immediate: true },
)
const totals = (ms: { amount_minor: number; currency: string }[]) => (ms.length ? ms.map((m) => formatMoney(m, locale.value)).join(' · ') : '—')

// Planning cards: this month's budget, debt and savings (per currency, no FX).
const planLoad = useForm()
const overview = ref<Overview | null>(null)
watch(() => session.workspace?.id, () => planLoad.submit(async () => (overview.value = await getOverview())), { immediate: true })
const pct = (a: number, b: number) => (b > 0 ? Math.min(100, (a / b) * 100) : 0)

// Placeholders for the modules arriving in later phases, per workspace kind.
const modules = computed(() =>
  session.isBusiness ? ['salesInvoices', 'vat'] : ['investments'],
)
</script>

<template>
  <div class="page">
    <header class="page-header">
      <div class="stack" style="gap: 0.4rem">
        <p class="muted small">{{ $t('home.welcome', { name: session.user?.display_name }) }}</p>
        <h1 data-testid="workspace-name">{{ session.workspace?.name }}</h1>
        <div class="row">
          <UiBadge data-testid="workspace-kind" :variant="session.isBusiness ? 'default' : 'secondary'">
            {{ $t(`workspace.kind.${session.workspace?.kind ?? 'personal'}`) }}
          </UiBadge>
          <UiBadge variant="outline">{{ $t(`roles.${session.workspace?.role ?? 'viewer'}`) }}</UiBadge>
        </div>
      </div>
    </header>

    <ErrorBanner :error="load.error.value" />

    <EmptyState v-if="!load.busy.value && !load.error.value && !accounts.length" :title="$t('home.noAccounts')" :message="$t('home.noAccountsText')">
      <div v-if="session.canWrite" class="row" style="justify-content: center">
        <RouterLink to="/accounts">{{ $t('accounts.new') }}</RouterLink>
        <RouterLink to="/import">{{ $t('nav.import') }}</RouterLink>
      </div>
    </EmptyState>

    <div v-for="cur in currencies" :key="cur" class="grid-2" data-testid="home-ledger">
      <UiCard>
        <p class="muted small">{{ $t('home.balance') }} · {{ cur }}</p>
        <p class="big" data-testid="home-balance">{{ formatMoney(balance(cur), locale) }}</p>
      </UiCard>
      <UiCard>
        <p class="muted small">{{ $t('home.last30') }}</p>
        <p class="in">{{ $t('home.income') }}: {{ summaries[cur] ? formatMoney(summaries[cur].income, locale) : '—' }}</p>
        <p class="out">{{ $t('home.spending') }}: {{ summaries[cur] ? formatMoney(summaries[cur].spending, locale) : '—' }}</p>
      </UiCard>
      <UiCard>
        <p class="muted small">{{ $t('home.topCategories') }}</p>
        <p v-if="!topSpending(cur).length" class="muted small">—</p>
        <ul v-else class="cats">
          <li v-for="c in topSpending(cur)" :key="c.category">
            <RouterLink :to="{ path: '/transactions', query: { category: c.category } }">{{ c.category === 'none' ? $t('transactions.uncategorised') : c.category }}</RouterLink>
            <span class="num">{{ formatMoney(c.amount, locale) }}</span>
          </li>
        </ul>
      </UiCard>
    </div>

    <ErrorBanner :error="billsLoad.error.value" />
    <div v-if="bills" class="grid-2" data-testid="home-bills">
      <UiCard>
        <p class="muted small">{{ $t('home.openBills', { n: bills.open.count }) }}</p>
        <p class="big">{{ totals(bills.open.totals) }}</p>
        <RouterLink :to="{ path: '/bills', query: { tab: 'open' } }" class="small">{{ $t('nav.bills') }} →</RouterLink>
      </UiCard>
      <UiCard>
        <p class="muted small">{{ $t('home.overdue') }}</p>
        <p :class="['big', { out: bills.overdue.count }]" data-testid="home-overdue">{{ bills.overdue.count }}</p>
        <RouterLink :to="{ path: '/bills', query: { tab: 'overdue' } }" class="small">{{ totals(bills.overdue.totals) }} →</RouterLink>
      </UiCard>
      <UiCard>
        <p class="muted small">{{ $t('home.draftsToReview') }}</p>
        <p class="big" data-testid="home-drafts">{{ bills.drafts.count }}</p>
        <RouterLink :to="{ path: '/bills', query: { tab: 'draft' } }" class="small">{{ $t('bills.tabs.draft') }} →</RouterLink>
      </UiCard>
      <UiCard v-if="suggestions">
        <p class="muted small">{{ $t('home.suggestionsToReview') }}</p>
        <p class="big" data-testid="home-suggestions">{{ suggestions.n }}{{ suggestions.more ? '+' : '' }}</p>
        <RouterLink to="/reconciliation" class="small">{{ $t('nav.reconciliation') }} →</RouterLink>
      </UiCard>
    </div>

    <ErrorBanner :error="planLoad.error.value" />
    <div v-if="overview" class="grid-2" data-testid="home-planning">
      <UiCard>
        <p class="muted small">{{ $t('home.budgetMonth', { month: overview.month }) }}</p>
        <template v-if="overview.budget">
          <p class="big" data-testid="home-budget">{{ formatMoney(overview.budget.actual_total, locale) }} / {{ formatMoney(overview.budget.planned_total, locale) }}</p>
          <div class="bar"><div :class="['fill', overview.budget.over ? 'over' : overview.budget.warn ? 'warn' : 'ok']"
            :style="{ width: `${pct(overview.budget.actual_total.amount_minor, overview.budget.planned_total.amount_minor)}%` }" /></div>
          <p class="small muted">{{ $t('home.budgetLines', { ok: overview.budget.ok, warn: overview.budget.warn, over: overview.budget.over }) }}</p>
        </template>
        <p v-else class="muted small">{{ $t('budget.empty') }}</p>
        <RouterLink to="/budget" class="small">{{ $t('nav.budget') }} →</RouterLink>
      </UiCard>
      <UiCard>
        <p class="muted small">{{ $t('home.totalDebt') }}</p>
        <p class="big" data-testid="home-debt">{{ totals(overview.debt.map((d) => d.balance)) }}</p>
        <p class="small muted">{{ $t('home.monthlyObligations') }}: {{ totals(overview.debt.map((d) => d.monthly_payments)) }}</p>
        <RouterLink to="/obligations" class="small">{{ $t('nav.obligations') }} →</RouterLink>
      </UiCard>
      <UiCard>
        <p class="muted small">{{ $t('home.savingsProgress') }}</p>
        <p v-if="!overview.savings.length" class="big">—</p>
        <div v-for="sv in overview.savings" :key="sv.saved.currency">
          <p class="big" data-testid="home-savings">{{ formatMoney(sv.saved, locale) }} / {{ formatMoney(sv.target, locale) }}</p>
          <div class="bar"><div class="fill acc" :style="{ width: `${Math.min(100, Number(sv.progress_pct))}%` }" /></div>
        </div>
        <RouterLink to="/savings" class="small">{{ $t('nav.savings') }} →</RouterLink>
      </UiCard>
    </div>

    <div class="grid-2">
      <UiCard v-for="m in modules" :key="m">
        <div class="stack" style="gap: 0.5rem">
          <div class="row soon-head">
            <h3>{{ $t(`modules.${m}.title`) }}</h3>
            <span class="spacer" />
            <UiBadge variant="outline" class="soon-badge">{{ $t('common.soon') }}</UiBadge>
          </div>
          <p class="muted small">{{ $t(`modules.${m}.text`) }}</p>
        </div>
      </UiCard>
    </div>
  </div>
</template>

<style scoped>
/* Keep the "coming soon" badge on the title line even for long titles. */
.soon-head { flex-wrap: nowrap; }
.soon-badge { flex-shrink: 0; white-space: nowrap; }
.big {
  font-size: 1.6rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.in {
  color: var(--gain);
}
.out {
  color: var(--loss);
}
.cats {
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
}
.cats li {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}
.num {
  font-variant-numeric: tabular-nums;
}
.bar { height: 8px; border-radius: 999px; background: var(--border); overflow: hidden; margin: 0.3rem 0; }
.fill { height: 100%; border-radius: 999px; }
.fill.ok { background: var(--gain); }
.fill.warn { background: var(--warn); }
.fill.over { background: var(--loss); }
.fill.acc { background: var(--accent); }
</style>
